import { flash } from "@xola/ui-kit";
import clsx from "clsx";
import { isString, noop } from "lodash";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useAcceptJs } from "react-acceptjs";
import { AuthorizeNetBillingDetails, CardInputMethod, PaymentGatewayErrors } from "types";
import { getEnvironment } from "../../helpers/authNetPaymentHelper";
import { formatCardNumber, getCompatibleCardType, getCreditCardType } from "../../helpers/rawCardHelper";
import { useSeller } from "../../hooks/useAccount";
import { isAdditionalBillingInfoRequired } from "../../models/Seller";
import { PaymentFailedAlertModal } from "../PurchaseCreate/PaymentFailedAlertModal";
import { AuthNetAdditionalBillingDetailsModal } from "../PurchaseModification/AuthNetAdditionalBillingDetails";
import { PaymentInputRef, TokenizedCardData, TokenizePaymentParams } from "./PaymentInputRef";
import { getInitialCardValues, RawCreditCard, RawCreditCardInput, validateCard } from "./RawCreditCardInput";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface AuthorizeNetCardInputProps {
    className: string;
    onAdditionalDetailsSubmit: (tokenizePaymentParams: TokenizePaymentParams) => Promise<void>;
    onError: (error: string | null) => void;
}

export const AuthorizeNetCardInput = forwardRef<PaymentInputRef, AuthorizeNetCardInputProps>(
    ({ className = "", onError, onAdditionalDetailsSubmit }, ref) => {
        const seller = useSeller();
        const [creditCard, setCreditCard] = useState<RawCreditCard>(getInitialCardValues());
        const additionalBillInfoRequired = isAdditionalBillingInfoRequired(seller);
        const [showError, setShowError] = useState(false);
        const [processingError, setProcessingError] = useState<string | null>();
        const [isSubmitting, setIsSubmitting] = useState(false);
        const swipeBufferRef = useRef<string>("");
        const isSwipingRef = useRef(false);
        const [isSwiping, setIsSWipping] = useState(false);

        const authData = {
            apiLoginID: seller.defaultGateway?.credentials?.apiLoginId ?? "",
            clientKey: seller.defaultGateway?.publicKey ?? "",
        };

        const [additionalBillingDetailsState, setAdditionalBillingDetailsState] = useState<{
            isOpen: boolean;
            resolve: (tokenizePaymentParams: TokenizePaymentParams) => void;
        }>({
            isOpen: false,
            resolve: noop,
        });

        // We will disable Key down events for 2 seconds when swipping is in progress
        useEffect(() => {
            setTimeout(() => {
                isSwipingRef.current = false;
                setIsSWipping(false);
            }, 2000);
        }, [isSwiping]);

        useEffect(() => {
            window.addEventListener("keydown", handleKeyDown);

            return () => {
                window.removeEventListener("keydown", handleKeyDown);
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const parseSwipeData = (swipeData: string) => {
            const sanitizedData = swipeData.slice(1, -1);

            const parts = sanitizedData.split("^");

            if (parts.length >= 3) {
                let cardNumber = parts[0];
                const expiry = parts[2].slice(0, 4);

                const formattedExpiry = `${expiry.slice(2, 4)}${expiry.slice(0, 2)}`;
                const card = getCreditCardType(cardNumber);
                if (card) {
                    cardNumber = formatCardNumber(cardNumber, card);
                }

                setCreditCard({
                    cardNumber,
                    expiry: formattedExpiry,
                    cvv: "",
                    zipCode: "",
                    type: getCreditCardType(cardNumber)?.type ?? "",
                });
            }
        };

        // Sample string which we get after card swipe %B4111111111111111^SMITH/JOHN^2304101000000000?;4111111111111111=23041010000000000000?
        const handleCardSwipe = (e: KeyboardEvent) => {
            const validKeys = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "^", ";", "?", "%"];
            if (!validKeys.includes(e.key)) {
                return;
            }

            if (e.key === "%") {
                swipeBufferRef.current = "%";
                isSwipingRef.current = true;
                setIsSWipping(true);
            } else if (swipeBufferRef.current.startsWith("%")) {
                swipeBufferRef.current += e.key;

                if (e.key === "?") {
                    parseSwipeData(swipeBufferRef.current);
                    useCreatePurchaseStore.setState({ cardInputMethod: CardInputMethod.SWIPE });
                }
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            handleCardSwipe(e);
            if (isSwipingRef.current) {
                e.preventDefault();
            }
        };

        const tokenizeCard = async (billingDetails?: AuthorizeNetBillingDetails) => {
            const [cardNumber, expiryMonth, expiryYear, zipCode] = getAuthorizeNetCardValues(creditCard);
            const response = await dispatchData({
                cardData: {
                    cardNumber: cardNumber,
                    cardCode: creditCard.cvv,
                    month: expiryMonth,
                    year: expiryYear,
                    zip: zipCode,
                },
            });
            const result: TokenizePaymentParams = {
                response: { data: response, billingDetails },
                last4: cardNumber.slice(-4),
            };

            return result;
        };

        const { dispatchData } = useAcceptJs({ environment: getEnvironment(), authData });

        useImperativeHandle(ref, () => ({
            // Dummy function wont be used for authorize.net
            async confirm() {
                return true;
            },

            async tokenize() {
                if (isString(creditCard)) {
                    return null;
                }

                // Open Additional details modal if seller has additional info required configured, promise is resolved on submit
                if (additionalBillInfoRequired) {
                    return new Promise<TokenizePaymentParams>((resolve) => {
                        setAdditionalBillingDetailsState({
                            isOpen: true,
                            resolve: resolve,
                        });
                    });
                }

                return tokenizeCard() as Promise<TokenizedCardData>;
            },

            onPaymentError(error: string) {
                if (error === PaymentGatewayErrors.OPEN_ADDITIONAL_INFO_MODAL) {
                    setAdditionalBillingDetailsState({ isOpen: true, resolve: noop });
                    setShowError(true);
                } else {
                    setAdditionalBillingDetailsState({ isOpen: false, resolve: noop });
                    setProcessingError(error);
                }
            },
        }));

        const handleCardChange = (card: RawCreditCard) => {
            if (card.type) {
                card.type = getCompatibleCardType(card.type);
                if (!seller.acceptedCards.includes(card.type)) {
                    onError("is not acceptable");
                }
            }

            onError(validateAuthNetCard(card));
            setCreditCard(card);
        };

        const handleCloseAdditionalBillingDetails = (tokenizePaymentParams?: TokenizePaymentParams) => () => {
            if (tokenizePaymentParams) {
                additionalBillingDetailsState.resolve(tokenizePaymentParams);
            }

            setAdditionalBillingDetailsState({ isOpen: false, resolve: noop });
        };

        const handleBillingDetailsSubmit = async (billingDetails: AuthorizeNetBillingDetails) => {
            try {
                setIsSubmitting(true);
                const tokenizePaymentParams = await tokenizeCard(billingDetails);
                if (showError) {
                    onAdditionalDetailsSubmit(tokenizePaymentParams);
                    setShowError(false);
                } else {
                    setIsSubmitting(false);
                    return handleCloseAdditionalBillingDetails(tokenizePaymentParams)();
                }
            } catch (error: any) {
                flash.danger(error.message);
            }

            setIsSubmitting(false);
            handleCloseAdditionalBillingDetails()();
        };

        useEffect(() => {
            onError("is required");
        }, [onError]);

        return (
            <>
                <div
                    className={clsx(
                        "w-full rounded border border-gray-light text-base text-gray-darker hover:border-black hover:bg-gray-lighter focus:text-black active:text-black",
                        className,
                    )}
                >
                    <RawCreditCardInput card={creditCard} onChange={handleCardChange} />
                </div>
                {additionalBillingDetailsState.isOpen && (
                    <AuthNetAdditionalBillingDetailsModal
                        isOpen={additionalBillingDetailsState.isOpen}
                        isSubmitting={isSubmitting}
                        showError={showError}
                        onSubmit={handleBillingDetailsSubmit}
                        onClose={handleCloseAdditionalBillingDetails()}
                    />
                )}
                <PaymentFailedAlertModal error={processingError} onConfirm={() => setProcessingError(null)} />
            </>
        );
    },
);

const validateAuthNetCard = (card: RawCreditCard) => {
    const { cardNumberError, expiryError } = validateCard(card);

    let error = null;

    if (!cardNumberError.isValid) {
        error = "Card number is invalid";
    } else if (!expiryError.isValid) {
        error = "Expiry Date is invalid";
    }

    return error;
};

const getAuthorizeNetCardValues = (creditCard: RawCreditCard) => {
    const cardNumber = creditCard.cardNumber.replace(/\s/g, "");
    const expiryDate = creditCard.expiry.replace(/\D/g, "").slice(0, 4);
    const expiryMonth = expiryDate.slice(0, 2);
    const expiryYear = expiryDate.slice(2, 4);

    return [cardNumber, expiryMonth, expiryYear, creditCard.zipCode];
};
